import type { ReactNode } from 'react';
import { cn } from '@resource-kit/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@resource-kit/ui/react';

/**
 * `DisplayTable` — a read-only column table: hand it columns and rows, it
 * renders them.
 *
 * This repo had three tables and none of them was this one. `DataTable` is the
 * TanStack LIST surface — sorting, row selection, pagination, the column
 * picker — and reaching for it to show four static rows drags all of that in.
 * `JsonKeyValueTable` renders a key→value TREE, which is a different shape
 * entirely. So the order detail's line-item table was hand-rolled `<table>`
 * markup inside a widget, with its borders, paddings and alignment written out
 * by hand and reachable by no other surface; when the delivery job needed the
 * same table, the choice was to copy that markup or to name it. This is the
 * naming.
 *
 * It stays deliberately small, and the boundary is not arbitrary: everything
 * it does NOT do is something `DataTable` already owns. A second sortable,
 * selectable, paginated table is exactly how two list surfaces begin to drift,
 * and a caller who needs those needs the list surface, not this.
 *
 * Built on the shadcn `Table` primitives rather than bare `<table>` so it
 * inherits the design system's borders and hover states instead of restating
 * them — which is what the hand-rolled markup was doing.
 */
export interface DisplayColumn<Row> {
  /** React key and nothing else; the header carries the label. */
  key: string;
  header: ReactNode;
  /** Defaults to `left`. Numeric columns want `right`. */
  align?: 'left' | 'right' | 'center';
  cell: (row: Row, index: number) => ReactNode;
  /** Extra classes on this column's cells (not its header). */
  cellClassName?: string;
}

export interface DisplayTableProps<Row> {
  columns: readonly DisplayColumn<Row>[];
  rows: readonly Row[];
  /**
   * Rendered INSTEAD of the table when there is no row. A header row standing
   * over nothing reads as "still loading" or "filtered down to zero"; the
   * caller's own sentence is the honest render. Absent ⇒ nothing at all.
   */
  empty?: ReactNode;
  /** Accessible name for the table element. */
  ariaLabel?: string;
  /** Stable row key. Defaults to the index, which suits a static render. */
  rowKey?: (row: Row, index: number) => string;
  /** Extra classes on one row's `<tr>`; `undefined` adds none. */
  rowClassName?: (row: Row, index: number) => string | undefined;
  className?: string;
}

const ALIGN_CLASS = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
} as const;

export function DisplayTable<Row>({
  columns,
  rows,
  empty,
  ariaLabel,
  rowKey,
  rowClassName,
  className,
}: DisplayTableProps<Row>) {
  if (rows.length === 0) {
    return empty === undefined ? null : (
      <span className="text-sm text-muted-foreground">{empty}</span>
    );
  }

  return (
    /*
     * The overflow box is the component's, not the caller's. The detail
     * surface lays its slots out in a two-column grid at `md`, and a table
     * that cannot shrink widens the whole page instead of its own cell — the
     * page then scrolls sideways, which is the one thing no page may do.
     */
    <div className={cn('overflow-x-auto', className)}>
      <Table aria-label={ariaLabel}>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead
                key={column.key}
                className={cn(ALIGN_CLASS[column.align ?? 'left'], 'h-8 text-xs font-medium')}
              >
                {column.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow
              key={rowKey ? rowKey(row, index) : index}
              className={rowClassName?.(row, index)}
            >
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  className={cn(
                    ALIGN_CLASS[column.align ?? 'left'],
                    'py-1.5',
                    // Numbers line up only if their glyphs do.
                    column.align === 'right' && 'tabular-nums',
                    column.cellClassName,
                  )}
                >
                  {column.cell(row, index)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
